import {Component, ContentChild, Input, OnInit, TemplateRef, ViewEncapsulation,} from '@angular/core';
import {Router} from '@angular/router';
import {Observable, Subject} from 'rxjs';
import {map, startWith, switchMap, tap} from 'rxjs/operators';
import {BreadcrumbItemDirective} from '../breadcrumb-item.directive';
import {BreadcrumbDefinition, BreadcrumbService} from '../breadcrumbs.service';

/**
 * Цепочка длиннее этого числа сегментов сворачивается.
 * Порог именно ">", чтобы многоточие не занимало место одного скрытого сегмента.
 */
const COLLAPSE_THRESHOLD = 5;

/** Сколько сегментов хвоста всегда видно: два последних предка + текущий. */
const VISIBLE_TAIL = 3;

export interface BreadcrumbSegment {
  breadcrumb?: BreadcrumbDefinition;
  ellipsis?: boolean;
}

@Component({
    selector: 'app-breadcrumb',
    templateUrl: './breadcrumb.component.html',
    styleUrls: ['./breadcrumb.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class BreadcrumbComponent implements OnInit {
  segments$: Observable<BreadcrumbSegment[]>;
  separatorTemplate: TemplateRef<void>;
  /**
   * Breadcrumb item can be customized with this template
   * Template context is provided label, additional info, first and last indexes
   * Use cases:
   * 1) Add an icon along with label
   * 2) i18n. {{breadcrumb | translate}} or {{breadcrumb | transloco}}
   * 3) Change text case {{breadcrumb | titlecase}}
   */
  @ContentChild(BreadcrumbItemDirective, {static: false, read: TemplateRef})
  itemTemplate;
  /**
   * If true, breadcrumb is auto generated even without any mapping label
   * Default label is same as route segment
   */
  @Input() autoGenerate = true;
  /**
   * By default query params will be preserved with breadcrumbs
   */
  @Input() preserveQueryParams = true;
  /**
   * By default query fragments will be preserved with breadcrumbs
   */
  @Input() preserveFragment = true;
  /**
   * custom class provided by consumer to increase specificity
   * This will benefit to override styles that are conflicting
   */
  @Input() class = '';

  constructor(
    private breadcrumbService: BreadcrumbService,
    private router: Router
  ) {
  }

  private _separator = '›';

  /** true — скрытые сегменты раскрыты по клику на многоточие */
  private expanded = false;
  private expandedChanges = new Subject<boolean>();

  get separator() {
    return this._separator;
  }

  /**
   * separator between breadcrumbs, defaults to '/'.
   * User can customize separator either by passing a String or Template
   *
   * String --> Ex: <xng-breadcrumb separator="-"> </xng-breadcrumb>
   *
   * Template --> Ex: <xng-breadcrumb [separator]="separatorTemplate"> </xng-breadcrumb>
   * <ng-template #separatorTemplate><mat-icon>arrow_right</mat-icon></ng-template>
   */
  @Input('separator')
  set separator(value: string | TemplateRef<void>) {
    if (value instanceof TemplateRef) {
      this.separatorTemplate = value;
      this._separator = undefined;
    } else {
      this.separatorTemplate = undefined;
      this._separator = value || '›';
    }
  }

  ngOnInit() {
    const breadcrumbs$ = this.breadcrumbService.breadcrumbs$.pipe(
      map((breadcrumbs: BreadcrumbDefinition[]) => {
        return breadcrumbs
          .filter((breadcrumb: BreadcrumbDefinition) => {
            // Usually, breadcrumb list can contain a combination of auto generated and user specified labels
            // this filters autogenerated labels in case of "[autoGenerate]: false"
            if (this.autoGenerate) {
              return true;
            }
            return !breadcrumb.isAutoGeneratedLabel;
          })
          .map((breadcrumb: BreadcrumbDefinition) => {
            // Do not mutate breadcrumb as its source of truth.
            // There can be scenarios where we can have multiple xng-breadcrumb instances in page
            return {
              ...breadcrumb,
              queryParams: this.preserveQueryParams
                ? breadcrumb.queryParams
                : undefined,
              fragment: this.preserveFragment ? breadcrumb.fragment : undefined,
            };
          });
      })
    );

    this.segments$ = breadcrumbs$.pipe(
      // новая цепочка приходит свёрнутой, даже если предыдущую раскрыли
      tap(() => (this.expanded = false)),
      switchMap((breadcrumbs: BreadcrumbDefinition[]) =>
        this.expandedChanges.pipe(
          startWith(this.expanded),
          map((expanded: boolean) => this.collapse(breadcrumbs, expanded))
        )
      )
    );
  }

  /**
   * Оставляет корень + VISIBLE_TAIL последних сегментов, середину прячет за многоточие
   */
  private collapse(
    breadcrumbs: BreadcrumbDefinition[],
    expanded: boolean
  ): BreadcrumbSegment[] {
    if (expanded || breadcrumbs.length <= COLLAPSE_THRESHOLD) {
      return breadcrumbs.map((breadcrumb) => ({breadcrumb}));
    }

    return [
      {breadcrumb: breadcrumbs[0]},
      {ellipsis: true},
      ...breadcrumbs
        .slice(breadcrumbs.length - VISIBLE_TAIL)
        .map((breadcrumb) => ({breadcrumb})),
    ];
  }

  expand() {
    this.expanded = true;
    this.expandedChanges.next(true);
  }

  handleRoute(breadcrumb: BreadcrumbDefinition) {
    const routeLink = breadcrumb.routeInterceptor
      ? breadcrumb.routeInterceptor(breadcrumb.routeLink, breadcrumb)
      : breadcrumb.routeLink;
    const {queryParams, fragment} = breadcrumb;
    this.router.navigate([routeLink], breadcrumb.useRouterLinkOnly ? {} : {queryParams, fragment});
  }
}
